import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Users, Building2, TrendingUp, Activity, AlertTriangle,
  UserPlus, Calendar, Trophy, Dumbbell, Apple, Moon,
  ArrowUpRight, ArrowDownRight, RefreshCw, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminOverviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [atRiskUsers, setAtRiskUsers] = useState([]);
  const [featureAdoption, setFeatureAdoption] = useState([]);

  const adminKey = sessionStorage.getItem('aura_admin_key');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, atRiskRes, adoptionRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/users?limit=5&sort=created_at_desc`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/at-risk-users`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/feature-adoption`, { headers: { 'X-Admin-Key': adminKey } })
      ]);
      
      setStats(statsRes.data);
      setRecentUsers(usersRes.data.users || []);
      setAtRiskUsers(atRiskRes.data.users || []);
      setFeatureAdoption(adoptionRes.data.features || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, change, changeLabel, icon: Icon, color, link }) => (
    <Card className="bg-aura-surface border-white/10 hover:border-white/20 transition-all">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-1">{title}</p>
            <p className="text-3xl font-heading font-bold text-white">{value}</p>
            {change !== undefined && (
              <div className="flex items-center gap-1 mt-2">
                {change >= 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-green-400" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-400" />
                )}
                <span className={`text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {Math.abs(change)}%
                </span>
                <span className="text-xs text-aura-text-muted">{changeLabel}</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {link && (
          <Link to={link} className="mt-4 flex items-center text-sm text-aura-primary hover:underline">
            View details <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              Dashboard Overview
            </h1>
            <p className="text-aura-text-muted text-sm mt-1">
              Monitor your platform's health and growth
            </p>
          </div>
          <Button 
            onClick={fetchDashboardData}
            variant="outline" 
            className="border-white/10 text-white hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            change={stats?.userGrowth || 0}
            changeLabel="vs last week"
            icon={Users}
            color="bg-aura-primary"
            link="/admin/users"
          />
          <MetricCard
            title="Active Today"
            value={stats?.activeToday || 0}
            change={stats?.activityChange || 0}
            changeLabel="vs yesterday"
            icon={Activity}
            color="bg-green-500"
          />
          <MetricCard
            title="Organizations"
            value={stats?.totalOrgs || 0}
            icon={Building2}
            color="bg-purple-500"
            link="/admin/organizations"
          />
          <MetricCard
            title="New This Week"
            value={stats?.newThisWeek || 0}
            change={stats?.newUsersChange || 0}
            changeLabel="vs last week"
            icon={UserPlus}
            color="bg-amber-500"
          />
        </div>

        {/* Middle Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feature Adoption */}
          <Card className="bg-aura-surface border-white/10 lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-white uppercase tracking-wider text-lg">
                Feature Adoption
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {featureAdoption.length > 0 ? featureAdoption.map((feature, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {feature.name === 'Training' && <Dumbbell className="w-4 h-4 text-aura-primary" />}
                      {feature.name === 'Nutrition' && <Apple className="w-4 h-4 text-green-400" />}
                      {feature.name === 'Recovery' && <Moon className="w-4 h-4 text-purple-400" />}
                      {feature.name === 'Challenges' && <Trophy className="w-4 h-4 text-amber-400" />}
                      <span className="text-white text-sm">{feature.name}</span>
                    </div>
                    <span className="text-aura-text-muted text-sm">{feature.percentage}%</span>
                  </div>
                  <Progress value={feature.percentage} className="h-2 bg-white/10" />
                </div>
              )) : (
                <p className="text-aura-text-muted text-center py-4">No adoption data available</p>
              )}
            </CardContent>
          </Card>

          {/* At-Risk Users */}
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-white uppercase tracking-wider text-lg">
                At-Risk Users
              </CardTitle>
              <Badge className="bg-red-500/20 text-red-400 border-0">
                {atRiskUsers.length}
              </Badge>
            </CardHeader>
            <CardContent>
              {atRiskUsers.length > 0 ? (
                <div className="space-y-3">
                  {atRiskUsers.slice(0, 5).map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                      <div>
                        <p className="text-white text-sm">{user.name || user.email}</p>
                        <p className="text-xs text-aura-text-muted">
                          Last active: {user.lastActive ? format(parseISO(user.lastActive), 'MMM d') : 'Never'}
                        </p>
                      </div>
                      <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">
                        {user.daysInactive}d inactive
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <AlertTriangle className="w-8 h-8 text-aura-text-muted mx-auto mb-2" />
                  <p className="text-aura-text-muted text-sm">No at-risk users</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Users & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Signups */}
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-white uppercase tracking-wider text-lg">
                Recent Signups
              </CardTitle>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm" className="text-aura-primary">
                  View All
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentUsers.map((user, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {(user.name || user.email)?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{user.name || 'No name'}</p>
                        <p className="text-xs text-aura-text-muted">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={`${
                        user.role === 'athlete' ? 'bg-aura-primary/20 text-aura-primary' :
                        user.role === 'supporter' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-amber-500/20 text-amber-400'
                      } border-0 text-xs`}>
                        {user.role}
                      </Badge>
                      <p className="text-xs text-aura-text-muted mt-1">
                        {user.created_at ? format(parseISO(user.created_at), 'MMM d, h:mm a') : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-white uppercase tracking-wider text-lg">
                Activity Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <Dumbbell className="w-6 h-6 text-aura-primary mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.workoutsToday || 0}</p>
                  <p className="text-xs text-aura-text-muted">Sessions Today</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <Apple className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.mealsToday || 0}</p>
                  <p className="text-xs text-aura-text-muted">Meals Logged</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <Trophy className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.challengesCompleted || 0}</p>
                  <p className="text-xs text-aura-text-muted">Challenges Done</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5 text-center">
                  <Calendar className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.eventsToday || 0}</p>
                  <p className="text-xs text-aura-text-muted">Events Today</p>
                </div>
              </div>

              {/* Engagement Rate */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-aura-primary/20 to-purple-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">Daily Engagement Rate</span>
                  <span className="text-aura-primary font-bold">{stats?.engagementRate || 0}%</span>
                </div>
                <Progress value={stats?.engagementRate || 0} className="h-2 bg-white/10" />
                <p className="text-xs text-aura-text-muted mt-2">
                  {stats?.activeToday || 0} of {stats?.totalUsers || 0} users active today
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOverviewPage;
